import { MutationTree } from 'vuex';
import { NavigationState } from '@/store/navigation/index';
import { NavigationTreeItem } from '@/store/navigation/models/NavigationTreeItem';
import {
  Link,
  Navigation,
  NavigationItem,
  Popup,
} from '../../../types/contentful-api';
import { NavbarTransparentElement } from '@/models/navigation/NavbarTypes';
import { DKSpaceSource } from '@/models/DKSpaceSource';
import { DKPopup } from '@/models/DKPopup';
import { removeLinkSlash, removeUrlLocale } from '@/helpers/i18n';

export const mutations: MutationTree<NavigationState> = {
  setReady: (state: NavigationState) => {
    state.ready = true;
  },
  setNavigations: (state: NavigationState, payload: Navigation[]) => {
    state.navigations.data = payload;
  },
  setNavigationItem: (state: NavigationState, payload: NavigationItem) => {
    if (!payload) {
      return;
    }
    const navigationItem = state.navigationItems.data.find(
      (currentItem) => currentItem.sys.id === payload.sys.id
    );
    if (!navigationItem) {
      state.navigationItems.data.push(payload);
    }
  },
  setNavigationItems: (state: NavigationState, payload: NavigationItem[]) => {
    state.navigationItems.data = payload;
  },
  setLinks: (state: NavigationState, payload: DKSpaceSource<Link>[]) => {
    state.links.data = payload;
  },
  setLink: (state: NavigationState, payload) => {
    if (!payload) {
      return;
    }
    const link = state.links.data.find(
      (currentLink) => currentLink.sys.id === payload.sys.id
    );
    if (!link) {
      state.links.data.push(payload);
    }
  },
  setCurrentLink: (state: NavigationState, payload) => {
    state.currentLink = payload;
  },
  setCurrentLinkByPath: (state: NavigationState, path: string) => {
    const cleanPath = removeLinkSlash(removeUrlLocale(path));
    const link = state.links.data
      .filter((linkItem: Link) => !!linkItem?.link)
      .find(
        (linkItem: Link) => cleanPath === removeLinkSlash(linkItem.link || '')
      );
    state.currentLink = link || null;
  },
  setCurrentLinkBySlug: (state: NavigationState, payload) => {
    state.currentLink =
      state.links.data.find(
        (link: Link) =>
          link?.link &&
          link?.link !== '/' &&
          (payload.endsWith(link.link) || payload.endsWith(`${link.link}/`))
      ) || null;
  },
  setPopupsCollection: (
    state: NavigationState,
    payload: {
      id: string;
      data: Popup;
    }
  ) => {
    state.popupsCollection[payload.id] = payload.data;
  },
  setCurrentSlugs: (state: NavigationState, payload): void => {
    state.currentSlugs = payload;
  },
  resetCurrentSlugs: (state: NavigationState): void => {
    state.currentSlugs = null;
  },
  setCurrentPopup: (state: NavigationState, payload: DKPopup | null) => {
    state.currentPopup = payload;
  },
  setCurrentNavigationTreeItem: (state: NavigationState, payload) => {
    state.currentNavigationTreeItem = payload;
  },
  clearCurrentNavigationTreeItem: (state: NavigationState) => {
    state.currentNavigationTreeItem = null;
  },
  openNavigationItem: (state: NavigationState, id: string) => {
    const navigationItem = state.navigationItems.data.find(
      (currentItem) => currentItem.sys.id == id
    );
    if (navigationItem) {
      navigationItem.expanded = true;
    }
    if (state.currentNavigationTreeItem) {
      state.currentNavigationTreeItem.expanded = true;
    }
  },
  closeNavigationItem: (state: NavigationState, id: string) => {
    const navigationItem = state.navigationItems.data.find(
      (currentItem) => currentItem.sys.id == id
    );
    if (navigationItem) {
      navigationItem.expanded = false;
    }
    if (state.currentNavigationTreeItem) {
      state.currentNavigationTreeItem.expanded = false;
    }
  },
  closeAllNavigationItems: (state: NavigationState) => {
    state.navigationItems.data.forEach(
      (navigationItem) => (navigationItem.expanded = false)
    );
  },
  toggleNavigationItem: (
    state: NavigationState,
    navigationTreeItem: NavigationTreeItem
  ) => {
    state.navigationItems.data.forEach((navigationItem) => {
      if (navigationItem.sys.id === navigationTreeItem.id) {
        navigationItem.expanded = !navigationItem.expanded;
      }
      if (!navigationTreeItem.breadcrumbs.includes(navigationItem.sys.id)) {
        navigationItem.expanded = false;
      }
    });
  },
  openTree: (
    state: NavigationState,
    navigationTreeItem: NavigationTreeItem
  ) => {
    state.navigationItems.data.forEach((navigationItem) => {
      const same = navigationItem.sys.id === navigationTreeItem.id;
      navigationItem.expanded =
        !same && navigationTreeItem.breadcrumbs.includes(navigationItem.sys.id);
    });
  },
  closeTree: (state: NavigationState) => {
    state.currentNavigationTreeItem = null;
    state.locationSwitcherExpanded = false;
    state.navigationItems.data.forEach(
      (navigationItem) => (navigationItem.expanded = false)
    );
  },
  openLocationSwitcher: (state: NavigationState) => {
    state.locationSwitcherExpanded = true;
  },
  closeLocationSwitcher: (state: NavigationState) => {
    state.locationSwitcherExpanded = false;
  },
  toggleLocationSwitcher: (state: NavigationState) => {
    state.locationSwitcherExpanded = !state.locationSwitcherExpanded;
  },
  toggleLanguageSwitcher: (state: NavigationState) => {
    state.languageSwitcherExpanded = !state.languageSwitcherExpanded;
  },
  closeLanguageSwitcher: (state: NavigationState) => {
    state.languageSwitcherExpanded = false;
  },
  toggleSearch: (state: NavigationState) => {
    state.searchExpanded = !state.searchExpanded;
  },
  closeSearch: (state: NavigationState) => {
    state.searchExpanded = false;
  },
  toggleSidebar: (state: NavigationState) => {
    state.sidebarExpanded = !state.sidebarExpanded;
  },
  closeSidebar: (state: NavigationState) => {
    state.sidebarExpanded = false;
  },
  setActiveNavLinkId: (state: NavigationState, id: string) => {
    state.activeNavLinkId = id;
  },
  setNavbarIsHidden(state: NavigationState, value: boolean) {
    state.navbarHidden = value;
  },
  setNavbarTransparentElement(
    state: NavigationState,
    payload: NavbarTransparentElement
  ) {
    state.transparentElement = payload;
  },
};
